/**
 * Breadcrumbs Component
 *
 * Accessible navigation breadcrumb trail showing the user's location.
 * Uses proper ARIA attributes for screen readers.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface BreadcrumbItem {
  /**
   * Display label
   */
  label: string;
  /**
   * Link href (optional for current page)
   */
  href?: string;
  /**
   * Icon to display before label
   */
  icon?: React.ReactNode;
}

export interface BreadcrumbsProps extends ComponentPropsWithoutRef<'nav'> {
  /**
   * Array of breadcrumb items
   */
  items: BreadcrumbItem[];
  /**
   * Show home icon for first item
   * @default true
   */
  showHomeIcon?: boolean;
  /**
   * Separator between items
   * @default ChevronRight
   */
  separator?: React.ReactNode;
  /**
   * Max items to show before collapsing
   * @default undefined (show all)
   */
  maxItems?: number;
}

/**
 * Breadcrumbs Component
 *
 * @example
 * ```tsx
 * <Breadcrumbs
 *   items={[
 *     { label: 'Dashboard', href: '/dashboard' },
 *     { label: 'Produkte', href: '/products' },
 *     { label: 'Premium Headphones' }
 *   ]}
 * />
 * ```
 */
const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(
  (
    {
      className,
      items,
      showHomeIcon = true,
      separator,
      maxItems,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Handle collapsing if maxItems is set
    const displayItems = maxItems && items.length > maxItems
      ? [
          ...items.slice(0, 1),
          { label: '...', href: undefined },
          ...items.slice(-(maxItems - 2)),
        ]
      : items;

    const defaultSeparator = <ChevronRight className="h-4 w-4" aria-hidden="true" />;

    return (
      <nav
        ref={ref}
        aria-label={t('a11y.breadcrumb')}
        className={cn('flex items-center space-x-1 text-sm', className)}
        {...props}
      >
        <ol className="flex items-center space-x-1">
          {displayItems.map((item, index) => {
            const isLast = index === displayItems.length - 1;
            const isEllipsis = item.label === '...';

            return (
              <li key={index} className="flex items-center space-x-1">
                {/* Item */}
                {item.href && !isEllipsis ? (
                  <a
                    href={item.href}
                    className={cn(
                      // BFSG compliant 44px touch target
                      'inline-flex h-11 items-center gap-1.5 px-2',
                      'rounded-md text-neutral-600 transition-colors',
                      'hover:text-neutral-900',
                      // Focus ring - WCAG AA
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
                    )}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {/* Home Icon */}
                    {index === 0 && showHomeIcon && (
                      <Home className="h-4 w-4" aria-hidden="true" />
                    )}
                    {/* Custom Icon */}
                    {item.icon && <span aria-hidden="true">{item.icon}</span>}
                    {/* Label */}
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <span
                    className={cn(
                      'inline-flex h-11 items-center gap-1.5 px-2',
                      isLast
                        ? 'font-medium text-neutral-900'
                        : 'text-neutral-500'
                    )}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {/* Home Icon */}
                    {index === 0 && showHomeIcon && !isEllipsis && (
                      <Home className="h-4 w-4" aria-hidden="true" />
                    )}
                    {/* Custom Icon */}
                    {item.icon && <span aria-hidden="true">{item.icon}</span>}
                    {/* Label */}
                    <span>{item.label}</span>
                  </span>
                )}

                {/* Separator */}
                {!isLast && (
                  <span className="text-neutral-400">
                    {separator || defaultSeparator}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
);

Breadcrumbs.displayName = 'Breadcrumbs';

export { Breadcrumbs };
